import { Button, ButtonProps } from "@mui/material";
import * as Sentry from "@sentry/react";
import { useTranslate } from "@tolgee/react";
import React from "react";
import { useLanguages } from "../../common/translations/useLanguages.js";
import { useUrlSearchParams } from "../commons/hooks/useUrlSearchParams.js";
import { ORGANISATION_TYPE_URL_SEARCH_PARAM } from "../constants.js";
import type { CreateableOrganisationType } from "./isValidOrganisationType.js";
import { useAuth } from "./useAuth.js";

interface Props {
  prefill?:
    | {
        email: string;
        firstName?: string;
        lastName?: string;
        salutation?: string;
      }
    | undefined;
  organisationType?: CreateableOrganisationType | undefined;
  variant?: ButtonProps["variant"];
  color?: ButtonProps["color"];
}

export const RegisterButton = ({
  prefill,
  organisationType,
  variant = "contained",
  color = "secondary",
}: Props) => {
  const { auth } = useAuth();
  const { t } = useTranslate(["Global"]);
  const { currentLanguage } = useLanguages();
  const { setUrlSearchParams } = useUrlSearchParams();

  const handleClick = React.useCallback(() => {
    if (organisationType) {
      setUrlSearchParams({
        [ORGANISATION_TYPE_URL_SEARCH_PARAM]: organisationType,
      });
    }

    setTimeout(async () => {
      try {
        const url = new URL(
          await auth.createRegisterUrl({ locale: currentLanguage.locale })
        );
        if (prefill) {
          const params = {
            email: prefill.email,
            firstName: prefill.firstName,
            lastName: prefill.lastName,
            "user.attributes.salutation": prefill.salutation,
          };
          for (const [name, value] of Object.entries(params)) {
            if (value) url.searchParams.set(name, value);
          }
        }
        window.open(url.toString(), "_self");
      } catch (e) {
        Sentry.captureException(e);
      }
    });
  }, [
    auth,
    currentLanguage.locale,
    organisationType,
    prefill,
    setUrlSearchParams,
  ]);

  return (
    <Button
      variant={variant}
      color={color}
      disableElevation
      fullWidth
      data-testid="signup-register"
      onClick={handleClick}
    >
      {t("Signup", { ns: "Global" })}
    </Button>
  );
};
